import React from "react";
import { useNavigate } from "react-router-dom";
import AnalyticsProcessWrapper from "./analyticsProcessWidgets.style";
import { Spinner } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";

const PROJECT_TYPE_META = {
  gemeo_digital: { name: "Gêmeo Digital", icon: "bi-box",         color: "#035184" },
  tour_360:      { name: "Tour 360°",     icon: "bi-arrow-repeat", color: "#1067b9" },
  filmagens:     { name: "Filmagens",     icon: "bi-camera-reels", color: "#2d6d82" },
  fotos:         { name: "Fotos",         icon: "bi-camera",       color: "#39474b" },
  geoview:       { name: "GeoView",      icon: "bi-map",          color: "#0d9488" },
};

const formatTokens = count => {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000)     return `${(count / 1_000).toFixed(0)}k`;
  return String(count);
};

const statusTier = pct => {
  if (pct <= 50) return "ok";
  if (pct <= 75) return "warn";
  return "crit";
};

const resolveTokenLimit = plan => {
  if (!plan) return 100_000;
  if (typeof plan.ai_feature === "number" && plan.ai_feature >= 1000) return plan.ai_feature;
  switch (plan.name) {
    case "Básico":    return 100_000;
    case "Premium":   return 1_000_000;
    case "Horizonte": return 5_000_000;
    case "Admin":     return 10_000_000;
    default:
      if (typeof plan.ai_feature === "number" && plan.ai_feature >= 1000) return plan.ai_feature;
      return 100_000;
  }
};

const UsageCard = ({ icon, label, value, pct, showBar }) => {
  const tier = pct != null ? statusTier(pct) : "neutral";
  return (
    <div className={`usage-card usage-card--${tier}`}>
      <i className={`bi ${icon} usage-card__icon usage-card__icon--${tier}`} />
      <div className="usage-card__label">{label}</div>
      <div className="usage-card__value">{value}</div>
      {showBar && pct != null && (
        <div className="usage-card__bar">
          <div
            className={`usage-card__bar-fill usage-card__bar-fill--${tier}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
};

const activityTypeLabels = {
  upload:         "Enviou arquivo",
  download:       "Baixou arquivo",
  create_project: "Criou projeto",
  delete_project: "Excluiu projeto",
  share:          "Compartilhou",
  view:           "Visualizou",
  login:          "Fez login",
  logout:         "Fez logout",
  report:         "Gerou relatório",
  ai_chat:        "Usou assistente IA",
  conversion:     "Converteu arquivo"
};

const AnalyticsProcessWidgets = ({ planData, azureUsageData, projectsData, activityData }) => {
  const navigate = useNavigate();

  const totalStorage = planData?.storage || 0;
  const usedStorage  = azureUsageData?.total_size || 0;
  const storagePct   = totalStorage > 0 ? (usedStorage / totalStorage) * 100 : 0;

  const maxProjects  = planData?.max_projects || 0;
  const projectStats = azureUsageData?.project_stats || {};
  const totalProjects = Object.values(projectStats).reduce((acc, p) => acc + p.subfolders, 0) || 0;
  const projectsPct   = maxProjects > 0 ? (totalProjects / maxProjects) * 100 : 0;

  const tokenLimit = resolveTokenLimit(planData);
  const aiCount    = azureUsageData?.ai_count || 0;
  const tokenPct   = tokenLimit > 0 ? Math.min(100, (aiCount / tokenLimit) * 100) : 0;

  const fileCount  = azureUsageData?.file_count || 0;
  const isTopPlan  = planData?.name === "Admin" || planData?.name === "Horizonte";

  const [usageMetric, setUsageMetric] = React.useState("storage");

  const projectTypes = Object.entries(PROJECT_TYPE_META).map(([key, meta]) => ({
    key,
    ...meta,
    count: projectStats?.[key]?.subfolders || 0,
    size:  projectStats?.[key]?.size || 0
  }));

  const allProjects = React.useMemo(() => {
    if (!projectsData) return [];
    const flat = [];
    for (const [type, items] of Object.entries(projectsData)) {
      if (!Array.isArray(items)) continue;
      for (const item of items) {
        flat.push({
          name:      item.name || item.project_name || "Sem nome",
          type,
          date:      item.date || item.created_at,
          thumbnail: item.thumbnail || null,
          conversor: item.conversor || null
        });
      }
    }
    flat.sort((a, b) => new Date(b.date) - new Date(a.date));
    return flat;
  }, [projectsData]);

  const conversionsInProgress = React.useMemo(() => {
    return allProjects.filter(p => {
      if (!p.conversor) return false;
      const status   = String(p.conversor.status || "").toLowerCase();
      const progress = typeof p.conversor.progress === "number" ? p.conversor.progress : null;
      const terminal = ["concluído", "concluido", "completed", "done", "erro", "error", "timeout"];
      if (terminal.some(kw => status.includes(kw))) return false;
      if (progress !== null) return progress < 100;
      return !!status;
    });
  }, [allProjects]);

  if (!planData || !azureUsageData) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" role="status" style={{ color: "#035184" }} />
      </div>
    );
  }

  // Calcula totais para barras por tipo
  const totalSizeAll  = azureUsageData.total_size || 0;
  const maxCount      = Math.max(...projectTypes.map(t => t.count), 1);
  const maxSize       = Math.max(...projectTypes.map(t => t.size), 0.001);

  return (
    <AnalyticsProcessWrapper className="w-100">

      {/* ─── Plano ─── */}
      <div className="plan-hero">
        <div className="plan-hero__content">
          <div className="plan-hero__badge">
            <div className="plan-hero__icon">
              <i className="bi bi-award-fill" />
            </div>
            <div>
              <div className="plan-hero__label">Plano atual</div>
              <h5 className="plan-hero__name">{planData.name || "Básico"}</h5>
            </div>
          </div>

          <div className="plan-hero__stats">
            <div className="plan-hero__stat">
              <span className="plan-hero__stat-label">Storage</span>
              <span className="plan-hero__stat-value">{totalStorage} GB</span>
            </div>
            <div className="plan-hero__stat">
              <span className="plan-hero__stat-label">Projetos</span>
              <span className="plan-hero__stat-value">{maxProjects || "∞"}</span>
            </div>
            <div className="plan-hero__stat">
              <span className="plan-hero__stat-label">Relatórios</span>
              <span className="plan-hero__stat-value">{planData.max_reports || "∞"}</span>
            </div>
            <div className="plan-hero__stat">
              <span className="plan-hero__stat-label">Tokens IA</span>
              <span className="plan-hero__stat-value">{formatTokens(tokenLimit)}</span>
            </div>
          </div>

          {!isTopPlan && (
            <button className="plan-hero__upgrade" onClick={() => navigate("/plans")}>
              <i className="bi bi-rocket-takeoff me-1" />
              Upgrade
            </button>
          )}
        </div>
      </div>

      {/* ─── Uso ─── */}
      <div className="dash-section">
        <div className="dash-section__title">Resumo do uso</div>
        <div className="usage-grid">
          <UsageCard
            icon="bi-hdd-fill"
            label="Armazenamento"
            value={`${storagePct.toFixed(1)}% de ${totalStorage} GB`}
            pct={storagePct}
            showBar
          />
          <UsageCard
            icon="bi-folder-fill"
            label="Projetos"
            value={`${totalProjects} de ${maxProjects || "∞"}`}
            pct={maxProjects > 0 ? projectsPct : null}
            showBar={maxProjects > 0}
          />
          <UsageCard
            icon="bi-file-earmark-fill"
            label="Arquivos"
            value={String(fileCount)}
            pct={null}
            showBar={false}
          />
          <UsageCard
            icon="bi-robot"
            label="Tokens IA"
            value={`${tokenPct.toFixed(1)}% de ${formatTokens(tokenLimit)}`}
            pct={tokenPct}
            showBar
          />
        </div>
      </div>

      {/* ─── Uso por tipo ─── */}
      <div className="dash-section">
        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: "0.875rem" }}>
          <div className="dash-section__title" style={{ margin: 0, border: "none", paddingBottom: 0 }}>
            Uso por tipo de projeto
          </div>
          <div className="metric-toggle">
            <button
              type="button"
              className={`metric-toggle__btn ${usageMetric === "storage" ? "metric-toggle__btn--active" : ""}`}
              onClick={() => setUsageMetric("storage")}
            >
              Espaço (GB)
            </button>
            <button
              type="button"
              className={`metric-toggle__btn ${usageMetric === "projects" ? "metric-toggle__btn--active" : ""}`}
              onClick={() => setUsageMetric("projects")}
            >
              Pastas
            </button>
          </div>
        </div>

        <div className="project-type-list">
          {projectTypes
            .sort((a, b) => usageMetric === "storage" ? b.size - a.size : b.count - a.count)
            .map(type => {
              const pct = usageMetric === "storage"
                ? (maxSize > 0 ? (type.size / maxSize) * 100 : 0)
                : (maxCount > 0 ? (type.count / maxCount) * 100 : 0);
              const detail = usageMetric === "storage"
                ? `${type.size.toFixed(2)} GB`
                : `${type.count} pasta${type.count !== 1 ? "s" : ""}`;
              return (
                <div key={type.key} className="project-type-item">
                  <div className="project-type-item__header">
                    <div className="project-type-item__left">
                      <div
                        className="project-type-row__icon"
                        style={{ background: type.color + "18", color: type.color }}
                      >
                        <i className={`bi ${type.icon}`} />
                      </div>
                      <span className="project-type-item__name">{type.name}</span>
                    </div>
                    <span className="project-type-item__right">{detail}</span>
                  </div>
                  <div className="project-type-item__track">
                    <div
                      className="project-type-item__fill"
                      style={{ width: `${Math.min(pct, 100)}%`, background: type.color }}
                    />
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>

      {/* ─── Conversões em andamento (condicional) ─── */}
      {conversionsInProgress.length > 0 && (
        <div className="dash-section">
          <div className="dash-section__title">
            <i className="bi bi-gear-wide-connected me-1" style={{ color: "#f59e0b" }} />
            Conversões em andamento
          </div>
          {conversionsInProgress.map((p, idx) => {
            const normalizedType = p.type === "geotiff" || p.type === "ortomosaico" ? "geoview" : p.type;
            const meta = PROJECT_TYPE_META[normalizedType] || {};
            const progress = p.conversor.progress || 0;
            return (
              <div key={idx} className="conversion-item">
                <div
                  className="conversion-item__icon"
                  style={{ background: (meta.color || "#6c757d") + "18", color: meta.color || "#6c757d" }}
                >
                  <i className={`bi ${meta.icon || "bi-file"}`} />
                </div>
                <div className="conversion-item__info">
                  <div className="conversion-item__name">{p.name}</div>
                  <div className="conversion-item__track">
                    <div className="conversion-item__fill" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="conversion-item__status">{p.conversor.status}</div>
                </div>
                <div className="conversion-item__pct">{progress}%</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Projetos recentes + Atividade ─── */}
      <div className="two-col">

        {/* Projetos Recentes */}
        <div className="dash-section" style={{ marginBottom: 0 }}>
          <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: "0.875rem" }}>
            <div className="dash-section__title" style={{ margin: 0, border: "none", paddingBottom: 0 }}>
              Projetos recentes
            </div>
            {allProjects.length > 5 && (
              <button className="btn-link-brand" onClick={() => navigate("/projects")}>
                Ver todos →
              </button>
            )}
          </div>

          {allProjects.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-folder2-open" />
              <p>{projectsData ? "Nenhum projeto encontrado" : "Carregando..."}</p>
            </div>
          ) : (
            <div>
              {allProjects.slice(0, 5).map((project, idx) => {
                const normalizedType =
                  project.type === "geotiff" || project.type === "ortomosaico" ? "geoview" : project.type;
                const meta = PROJECT_TYPE_META[normalizedType] || {};
                return (
                  <div key={idx} className="recent-project-item">
                    <div
                      className="recent-project-item__thumb"
                      style={
                        project.thumbnail
                          ? {}
                          : { background: (meta.color || "#6c757d") + "18" }
                      }
                    >
                      {project.thumbnail ? (
                        <img src={project.thumbnail} alt={project.name} />
                      ) : (
                        <i className={`bi ${meta.icon || "bi-file"}`} style={{ color: meta.color || "#6c757d" }} />
                      )}
                    </div>
                    <div className="recent-project-item__info">
                      <div className="recent-project-item__name">{project.name}</div>
                      <div className="recent-project-item__type">{meta.name || project.type}</div>
                    </div>
                    <div className="recent-project-item__date">
                      {project.date
                        ? new Date(project.date).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
                        : "—"
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Atividade Recente */}
        <div className="dash-section" style={{ marginBottom: 0 }}>
          <div className="dash-section__title">Atividade recente</div>

          {!activityData || activityData.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-journal-text" />
              <p>Nenhuma atividade recente</p>
            </div>
          ) : (
            <div>
              {activityData.slice(0, 8).map((activity, idx) => {
                const label =
                  activityTypeLabels[activity.activity_type] ||
                  activityTypeLabels[activity.activity_category] ||
                  activity.activity_type ||
                  "Ação";
                return (
                  <div key={activity.id || idx} className="activity-item">
                    <div className="activity-item__dot" />
                    <div className="activity-item__body">
                      <div className="activity-item__label">
                        <strong>{label}</strong>
                        {activity.activity_description && (
                          <span style={{ color: "var(--ink-muted)", fontWeight: 400 }}>
                            {" — "}{activity.activity_description}
                          </span>
                        )}
                      </div>
                      <div className="activity-item__meta">
                        {activity.created_at
                          ? new Date(activity.created_at).toLocaleDateString("pt-BR", {
                              day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                            })
                          : "—"
                        }
                        {activity.project_name && (
                          <span> · {activity.project_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

    </AnalyticsProcessWrapper>
  );
};

export default AnalyticsProcessWidgets;
